import { Box, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure, TTableStructureFilter } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { getColumnFilterValue } from 'utils/FilterParams';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import { getGbpMonthlyExchangeRateListRequest } from 'redux/reducers/exchangeRates/reducer';
import {
	getGbpMonthlyExchangeRateList,
	getGbpMonthlyExchangeRateListLoading,
} from 'redux/reducers/exchangeRates/selectors';
import { getEmEnumsRequest } from 'redux/reducers/emManagement/reducer';
import { EEmEnums } from 'redux/reducers/emManagement/constants';
import { getEnumsCurrencyCode } from 'redux/reducers/emManagement/selectors';
import { tableStructure as defaultTableStructure } from './tableStructure';
import { TGbpMonthlyExchangeRateRow } from './types';
import { buildPeriodFilters, withPeriod } from '../../utils';
import { EExchangeRatesTabGroupLabel } from '../../constants';

/** The column whose separate filter value is sent as a `currency` entry of `filters`. */
const CURRENCY_FILTER_SELECTOR = 'currency';
/** The Date column, whose year-month picker leaves a `YYYY-MM` value in the url. */
const PERIOD_FILTER_SELECTOR = 'period';

export const GbpMonthlyExchangeRates: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);

	const list = useSelector(getGbpMonthlyExchangeRateList);
	const listLoading = useSelector(getGbpMonthlyExchangeRateListLoading);
	const currencyCode = useSelector(getEnumsCurrencyCode);

	useEffect(() => {
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.CURRENCY_CODE }));
	}, [dispatch]);

	const params = useMemo(() => queryString.parse(search), [search]);

	/** Read through the column, so switching it between a separate and a plain filter changes nothing here. */
	const currencyFilter = getColumnFilterValue(
		defaultTableStructure.find((column) => column.selector === CURRENCY_FILTER_SELECTOR),
		params,
	);

	const periodFilter = getColumnFilterValue(
		defaultTableStructure.find((column) => column.selector === PERIOD_FILTER_SELECTOR),
		params,
	);

	const loadList = useCallback(() => {
		const filters: TFilters = buildPeriodFilters(periodFilter);
		if (currencyFilter) {
			filters.push({ fieldName: CURRENCY_FILTER_SELECTOR, value: currencyFilter, operator: '=' });
		}

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.ASC });

		dispatch(
			getGbpMonthlyExchangeRateListRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				filters,
				orderBy,
			}),
		);
	}, [currencyFilter, dispatch, params, periodFilter]);

	useEffect(() => {
		loadList();
	}, [loadList]);

	/** Currency codes the backend supports, offered as the options of the Currency filter. */
	const currencyOptions = useMemo<TTableStructureFilter>(
		() =>
			(currencyCode ?? [])
				.filter((item) => item.value.length === 3)
				.map((item) => [item.value, item.displayName]),
		[currencyCode],
	);

	const structure = useMemo(
		() =>
			currentTableStructure.map((column) =>
				column.selector === CURRENCY_FILTER_SELECTOR
					? { ...column, filter: currencyOptions }
					: column,
			),
		[currencyOptions, currentTableStructure],
	);

	/** Keeps only the visibility flags, so the columns stay free of the derived filter options. */
	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TGbpMonthlyExchangeRateRow>[]) => {
			setCurrentTableStructure((previous) =>
				previous.map((column) => {
					const changed = newTableStructure.find((item) => item.selector === column.selector);
					return changed ? { ...column, show: changed.show } : column;
				}),
			);
		},
		[],
	);

	const items = useMemo(() => withPeriod(list), [list]);

	return (
		<EasTableWithFilter<TGbpMonthlyExchangeRateRow>
			tableStructure={structure}
			items={items}
			itemsIsLoading={listLoading}
			handleAction={() => undefined}
			onTableStructureChange={handleTableStructureChange}
		>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<Typography variant="body1" color="primary">
					Resources / Reference Tables / Exchange Rates /{' '}
					<b>{EExchangeRatesTabGroupLabel.GBP_MONTHLY_EXCHANGE_RATES}</b>
				</Typography>
			</Box>
		</EasTableWithFilter>
	);
};
